import React, { cloneElement } from "react";
import {
  Button,
  Datagrid,
  Filter,
  FunctionField,
  ReferenceField,
  ReferenceInput,
  ResourceContextProvider,
  sanitizeListRestProps,
  SearchInput,
  SelectInput,
  ShowProps,
  TextField,
  TopToolbar,
  useListContext,
} from "react-admin";
import IconPlus from "@material-ui/icons/Add";
import SanitizedGrid from "components/SanitizedGrid";
import { Grid, Typography } from "@material-ui/core";
import CustomDateField from "components/CustomDateField";
import StatusField from "components/StatusField";
import PostBatchInvoiceButton from "./components/PostBatchInvoiceButton";
import CustomEmptyComponent from "components/CustomEmptyComponent";
import { PaddingDenseStyled2List } from "../../components/ListWrapper";
import { StyledButton } from "../../components/ListShowButton";
import ActionsMenu from "../../components/ActionsMenu";
import CustomPagination from "../../components/CustomPagination";

enum RateChartType {
  regularRateChart = "regular",
  ccaRateChart = "cca",
}

const ThisFilter = (props: any) => {
  return (
    <Filter {...props}>
      <SearchInput source="search" alwaysOn />
      <ReferenceInput
        label="Regular Academic Year"
        source="AcademicYearId"
        reference="academicYears"
        filter={{
          SchoolId: Number(props.filterValues.SchoolId),
        }}
        alwaysOn
      >
        <SelectInput optionText="name" optionId="id" />
      </ReferenceInput>
    </Filter>
  );
};

const CcaPricingFilter = (props: any) => {
  return (
    <Filter {...props}>
      <SearchInput source="search" alwaysOn />
      <ReferenceInput
        label="Cca Academic Year"
        source="CcaAcademicYearId"
        reference="ccaAcademicYears"
        filter={{
          SchoolId: Number(props.filterValues.SchoolId),
        }}
        alwaysOn
      >
        <SelectInput optionText="name" optionId="id" />
      </ReferenceInput>
    </Filter>
  );
};

const ListActions = (props: any) => {
  const {
    className,
    exporter,
    filters,
    maxResults,
    history,
    rateChartType,
    ...rest
  } = props;
  const { resource, displayedFilters, filterValues, showFilter } =
    useListContext();

  let actionUrl = "/createRatecharts";
  let buttonLabel = "Create Rate Chart";

  switch (rateChartType) {
    case RateChartType.regularRateChart:
      actionUrl = `/ratecharts/create?source=${JSON.stringify({
        schoolId: Number(props.filterValues?.SchoolId),
      })}&schoolId=${Number(props.filterValues?.SchoolId)}`;
      buttonLabel = "Create Regular Rate Chart";
      break;
    case RateChartType.ccaRateChart:
      actionUrl = `/ccaRatecharts/create?source=${JSON.stringify({
        schoolId: Number(props.filterValues?.SchoolId),
      })}&schoolId=${Number(props.filterValues?.SchoolId)}`;
      buttonLabel = "Create CCA Rate Chart";
      break;
    default:
      break;
  }

  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {filters &&
        cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: "button",
        })}

      <Button onClick={() => history.push(actionUrl)} label={buttonLabel}>
        <IconPlus />
      </Button>
    </TopToolbar>
  );
};

const CcaPricing = (props: ShowProps) => {
  const EditPricingButton = ({ id }: any) => {
    return (
      <StyledButton
        href={`/#/ccaRateCharts/${id}?schoolId=${props.id}`}
        variant="outlined"
      >
        Edit
      </StyledButton>
    );
  };

  const ShowPricingButton = ({ id }: any) => {
    return (
      <StyledButton
        href={`/#/ccaRateCharts/${id}/show?schoolId=${props.id}`}
        variant="outlined"
      >
        Show
      </StyledButton>
    );
  };

  return (
    <FunctionField
      render={(record: any) =>
        record.isCcaSettingEnable ? (
          <Grid item xs={12}>
            <ResourceContextProvider value="ccaRateCharts">
              <PaddingDenseStyled2List
                {...props}
                exporter={false}
                hasCreate={false}
                filters={<CcaPricingFilter />}
                filterDefaultValues={{ SchoolId: parseInt(props.id) }}
                bulkActionButtons={false}
                title=" "
                actions={
                  <ListActions
                    history={props.history}
                    rateChartType={RateChartType.ccaRateChart}
                  />
                }
                pagination={<CustomPagination />}
              >
                <Datagrid>
                  <TextField source="name" label="CCA Chart Name" />
                  <ReferenceField
                    source="ccaAcademicYearId"
                    reference="ccaAcademicYears"
                    label="CCA Academic Year"
                    emptyText="-"
                  >
                    <TextField source="name" />
                  </ReferenceField>
                  <CustomDateField
                    source="invoiceIssuedAt"
                    label="Invoice Issued At"
                    emptyText="-"
                  />
                  <CustomDateField
                    source="startedAt"
                    label="Pricing Start At"
                    emptyText="-"
                  />
                  <CustomDateField
                    source="endedAt"
                    label="Pricing End At"
                    emptyText="-"
                  />
                  <StatusField source="status" />
                  <FunctionField
                    label="Action"
                    render={(record: any) => {
                      return (
                        <ActionsMenu
                          render={[
                            <ShowPricingButton id={record.id} />,
                            record.status === "issued" &&
                              localStorage
                                .getItem("roles")
                                .includes("superadmin") && (
                                <PostBatchInvoiceButton
                                  rateChartId={record.id}
                                  resource="ccaRateCharts"
                                />
                              ),
                            record.status === "active" && (
                              <EditPricingButton id={record.id} />
                            ),
                          ]}
                        ></ActionsMenu>
                      );
                    }}
                  />
                </Datagrid>
              </PaddingDenseStyled2List>
            </ResourceContextProvider>
          </Grid>
        ) : (
          <CustomEmptyComponent
            resource="ccaRateCharts"
            title="The CCA setting is disabled"
            caption="Please enable the CCA setting in the school setting summary to show the CCA rate chart."
            hasCreate={false}
          />
        )
      }
    />
  );
};

const Pricing = (props: ShowProps) => {
  const EditPricingButton = ({ id }: any) => {
    return (
      <StyledButton
        href={`/#/rateCharts/${id}?schoolId=${props.id}`}
        variant="outlined"
      >
        Edit
      </StyledButton>
    );
  };

  const ShowPricingButton = ({ id }: any) => {
    return (
      <StyledButton
        href={`/#/rateCharts/${id}/show?schoolId=${props.id}`}
        variant="outlined"
      >
        Show
      </StyledButton>
    );
  };

  return (
    <SanitizedGrid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">Regular Rate Charts</Typography>
      </Grid>
      <Grid item xs={12}>
        <ResourceContextProvider value="rateCharts">
          <PaddingDenseStyled2List
            {...props}
            exporter={false}
            hasCreate={false}
            filterDefaultValues={{ SchoolId: Number(props.id) }}
            filters={<ThisFilter />}
            bulkActionButtons={false}
            title=" "
            actions={
              <ListActions
                history={props.history}
                rateChartType={RateChartType.regularRateChart}
              />
            }
            pagination={<CustomPagination />}
          >
            <Datagrid>
              <TextField source="name" label="Regular Chart Name" />
              <ReferenceField
                source="academicYearId"
                reference="academicYears"
                label="Regular Academic Year"
                emptyText="-"
              >
                <TextField source="name" />
              </ReferenceField>
              <CustomDateField
                source="invoiceIssuedAt"
                label="Invoice Issued At"
                emptyText="-"
              />
              <CustomDateField
                source="startedAt"
                label="Pricing Start At"
                emptyText="-"
              />
              <CustomDateField
                source="endedAt"
                label="Pricing End At"
                emptyText="-"
              />
              <StatusField source="status" />
              <FunctionField
                label="Action"
                render={(record: any) => {
                  return (
                    <ActionsMenu
                      render={[
                        <ShowPricingButton id={record.id} />,
                        record.status === "issued" &&
                          localStorage
                            .getItem("roles")
                            .includes("superadmin") && (
                            <PostBatchInvoiceButton
                              rateChartId={record.id}
                              resource="ratecharts"
                            />
                          ),
                        record.status === "active" && (
                          <EditPricingButton id={record.id} />
                        ),
                      ]}
                    />
                  );
                }}
              />
            </Datagrid>
          </PaddingDenseStyled2List>
        </ResourceContextProvider>
      </Grid>

      <Grid item xs={12} style={{ marginTop: 30 }}>
        <Typography variant="h6">CCA Rate Charts</Typography>
      </Grid>
      <Grid item xs={12}>
        <CcaPricing {...props} />
      </Grid>
    </SanitizedGrid>
  );
};

export default Pricing;
